import axios from "ts-axios-new";

export function decodeMapBlob(u8) {
    const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    let o = 0;
    const read = () => {
        const n = view.getUint32(o);
        o += 4;
        const slice = u8.subarray(o, o + n);
        o += n;
        return slice;
    };
    const cfgBytes = read();
    const mask = read();
    const finish = read();
    const config = JSON.parse(new TextDecoder().decode(cfgBytes));
    return {config, mask, finish};
}

const NO_CACHE_HEADERS = {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
};

function noCacheConfig(extra = {}) {
    return {
        ...extra,
        params: {
            _: Date.now(),
            ...(extra.params || {}),
        },
        headers: {
            ...NO_CACHE_HEADERS,
            ...(extra.headers || {}),
        },
    };
}

export async function fetchParts(jobId, partCount, concurrency = 4) {
    const parts = new Array(partCount);
    let next = 0;
    async function worker() {
        while (next < partCount) {
            const i = next++;
            const res = await axios.get(
                `/cms/v1/test/image/map/jobs/${jobId}/parts/${i}`,
                noCacheConfig({responseType: 'arraybuffer'}),
            );
            parts[i] = new Uint8Array(res.data);
        }
    }
    await Promise.all(Array.from({length: Math.min(concurrency, partCount)}, () => worker()));
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(total);
    let o = 0;
    for (const p of parts) {
        out.set(p, o);
        o += p.length;
    }
    return out;
}

function blobUrl(bytes, type) {
    return URL.createObjectURL(new Blob([bytes], {type}));
}

function dataUrlB64(b64, type = 'image/webp') {
    return `data:${type};base64,${b64}`;
}

async function loadChunked(imageId, lite = false) {
    const created = (await axios.post(
        `/cms/v1/test/image/${imageId}/map/jobs`,
        null,
        noCacheConfig({params: lite ? {lite: 1} : {}}),
    )).data.data;
    const jobId = created.jobId;
    const deadline = Date.now() + 120000;
    let status;
    while (Date.now() < deadline) {
        status = (await axios.get(
            `/cms/v1/test/image/map/jobs/${jobId}`,
            noCacheConfig(),
        )).data.data;
        if (status.status === 'ready') break;
        if (status.status === 'error') throw new Error(status.error || 'map job failed');
        await new Promise(r => setTimeout(r, 800));
    }
    if (!status || status.status !== 'ready') throw new Error('map job timeout');
    const blob = await fetchParts(jobId, status.partCount, 4);
    const decoded = decodeMapBlob(blob);
    const maskSrc = blobUrl(decoded.mask, 'image/webp');
    const finishSrc = (!lite && decoded.finish && decoded.finish.length)
        ? blobUrl(decoded.finish, 'image/webp')
        : null;
    return {
        data: decoded.config,
        maskSrc,
        finishSrc,
        revoke() {
            URL.revokeObjectURL(maskSrc);
            if (finishSrc) URL.revokeObjectURL(finishSrc);
        },
    };
}

async function loadInline(imageId, lite = false) {
    const res = await axios.get(
        `/cms/v1/test/image/${imageId}/map`,
        noCacheConfig({params: lite ? {lite: 1} : {}}),
    );
    const body = res.data.data;
    return {
        data: body.data,
        maskSrc: dataUrlB64(body.mask),
        finishSrc: body.finish ? dataUrlB64(body.finish) : null,
        revoke() {},
    };
}

/** @returns {Promise<{data: object, maskSrc: string, finishSrc: string|null, revoke: () => void}>} */
export async function loadMap(imageId, {lite = false} = {}) {
    const meta = (await axios.get(
        `/cms/v1/test/image/${imageId}/map/meta`,
        noCacheConfig(),
    )).data.data;
    if (meta.mode === 'inline') {
        return loadInline(imageId, lite);
    }
    return loadChunked(imageId, lite);
}
